/**
 * utils/menuSearch.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE definition of "which sections and products match what the customer
 * typed" for the sectioned storefronts (restaurant menus, brand catalogues).
 *
 * The brand page carried the same predicate three times (section filter,
 * product filter, "no results" test) and the restaurant page had no search
 * at all, so a hungry customer facing eight tabs of Taste Budz had to scroll
 * for a chicken bucket. Filter once here, render what comes back.
 */

export const DEFAULT_SEARCH_FIELDS = ["name", "description", "skuCode"];

export const normaliseQuery = (query) =>
  String(query || "")
    .trim()
    .toLowerCase();

const fieldText = (product, field) => {
  const v = product?.[field];
  if (v == null) return "";
  if (Array.isArray(v)) return v.map(String).join(" ");
  return String(v);
};

/**
 * Does a product match the query on any of the given fields?
 * Every space-separated word must appear somewhere ("beef large" finds
 * "Large Beef Pizza"); a single word is a plain substring match.
 */
export const productMatches = (product, query, fields = DEFAULT_SEARCH_FIELDS) => {
  const q = normaliseQuery(query);
  if (!q) return true;
  const haystack = fields
    .map((f) => fieldText(product, f))
    .join(" ")
    .toLowerCase();
  return q.split(/\s+/).every((word) => haystack.includes(word));
};

/**
 * Sections with only the products that are live AND match the query; a
 * section with nothing left is dropped. With an empty query this is exactly
 * the storefront's normal "enabled products only" view, so callers can use
 * the one result whether or not a search is in progress.
 *
 * @param {Array}  sections
 * @param {string} query
 * @param {object} [opts]
 * @param {(p: object) => boolean} [opts.isVisible]  default: product.enabled
 * @param {string[]} [opts.fields]
 * @returns {Array} shallow copies of the sections with `products` filtered
 */
export const filterSectionsByQuery = (sections, query, opts = {}) => {
  const { isVisible = (p) => p?.enabled, fields = DEFAULT_SEARCH_FIELDS } = opts;
  return (sections || [])
    .map((section) => {
      const products = (section?.products || []).filter(
        (p) => isVisible(p) && productMatches(p, query, fields),
      );
      return products.length ? { ...section, products } : null;
    })
    .filter(Boolean);
};

/** Total products across the filtered sections (for "12 dishes match"). */
export const countProducts = (sections) =>
  (sections || []).reduce((n, s) => n + (s?.products?.length || 0), 0);

export default filterSectionsByQuery;
